import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetListDto, AssetDetailDto, TransitEvaluation, Planets, AssetTransitScoreSummaryDto, Aspects, WindowProfileResult } from '../api';
import useAstroApiClient from '../hooks/useAstroApiClient';
import './InvestmentAnalysis.css';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Activity, AlertTriangle, ArrowLeft, BarChart3, Building2, CheckCircle, Coins, DollarSign, Loader2, PieChart, Search, Sparkles, Star, StarOff, Sun, TrendingDown, TrendingUp, X } from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import AstroChart from './AstroChart';
import { useNatalChart } from './astro/hooks/useNatalChart';
import { useHistory } from 'react-router';
import { degToDMSString, signPositions } from '../helpers/angle-utils';
import { getAssetDescription } from '../helpers/asset-translations';

type TransitRange = '30d' | '3m' | '6m' | '12m';

type TransitCandle = {
  key: string;
  startDate?: Date;
  endDate?: Date;
  open: number;
  close: number;
  high: number;
  low: number;
};

type AggregatedAspectForecast = {
  transit?: Planets;
  natal?: Planets;
  aspect?: Aspects;
  contribution: number;
  occurrences: number;
  forecast?: string;
};

const TRANSIT_RANGES: TransitRange[] = ['30d', '3m', '6m', '12m'];
const CANDLE_CHART_HEIGHT = 180;

const clampScore = (score?: number) => Math.max(0, Math.min(100, score ?? 0));

const addMonths = (date: Date, months: number) => {
  const nextDate = new Date(date);
  nextDate.setMonth(nextDate.getMonth() + months);
  return nextDate;
};

const getWeekStart = (date: Date) => {
  const nextDate = new Date(date);
  const day = nextDate.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  nextDate.setDate(nextDate.getDate() + diff);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
};

const buildTransitCandles = (evaluations: TransitEvaluation[]): TransitCandle[] => {
  const groups = new Map<string, TransitEvaluation[]>();

  evaluations
    .filter((evaluation) => evaluation.date)
    .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0))
    .forEach((evaluation) => {
      if (!evaluation.date) {
        return;
      }

      const bucketStart = getWeekStart(evaluation.date);
      const bucketKey = bucketStart.toISOString();
      const bucket = groups.get(bucketKey) ?? [];
      bucket.push(evaluation);
      groups.set(bucketKey, bucket);
    });

  return Array.from(groups.entries()).map(([key, bucket]) => {
    const sortedBucket = bucket.sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
    const scores = sortedBucket.map((item) => clampScore(item.percentAbsolute));

    return {
      key,
      startDate: sortedBucket[0]?.date,
      endDate: sortedBucket[sortedBucket.length - 1]?.date,
      open: scores[0] ?? 0,
      close: scores[scores.length - 1] ?? 0,
      high: scores.length ? Math.max(...scores) : 0,
      low: scores.length ? Math.min(...scores) : 0,
    };
  });
};

const aggregateAspectForecasts = (evaluations: TransitEvaluation[]): AggregatedAspectForecast[] => {
  const groupedForecasts = new Map<string, AggregatedAspectForecast>();

  evaluations.forEach((evaluation) => {
    evaluation.aspectForecasts?.forEach((aspect) => {
      const key = [aspect.transit, aspect.natal, aspect.aspect].join('|');
      const existing = groupedForecasts.get(key);

      if (existing) {
        existing.contribution += aspect.contribution ?? 0;
        existing.occurrences += 1;
        existing.forecast = existing.forecast ?? aspect.forecast ?? undefined;
        return;
      }

      groupedForecasts.set(key, {
        transit: aspect.transit,
        natal: aspect.natal,
        aspect: aspect.aspect,
        contribution: aspect.contribution ?? 0,
        occurrences: 1,
        forecast: aspect.forecast ?? undefined,
      });
    });
  });

  return Array.from(groupedForecasts.values())
    .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution))
    .slice(0, 5);
};

const InvestmentAnalysis: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<AssetDetailDto | null>(null);
  const [selectedRange, setSelectedRange] = useState<TransitRange>('30d');
  const [transitWindowResult, setTransitWindowResult] = useState<WindowProfileResult | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);
  const [selectedCandleIndex, setSelectedCandleIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState("ranking")
  const [assets, setAssets] = useState<AssetListDto[]>([]);
  const [favoriteAssets, setFavoriteAssets] = useState<AssetListDto[]>([]);
  const [, setFavoriteAssetsLoading] = useState(false);
  const [transitLoading, setTransitLoading] = useState(false);
  const [topScores, setTopScores] = useState<AssetTransitScoreSummaryDto | null>(null);
  const [topScoresLoading, setTopScoresLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isTogglingWatchlist, setIsTogglingWatchlist] = useState(false)

  const api = useAstroApiClient();
  const transitEvaluations = transitWindowResult?.transitEvaluations ?? [];
  const isLongRange = selectedRange !== '30d';
  const assetDescription = useMemo(
    () => getAssetDescription(selectedAsset?.translations, i18n.resolvedLanguage ?? i18n.language),
    [i18n.language, i18n.resolvedLanguage, selectedAsset?.translations],
  );

  const goError = () => {
    history.push("/error");
  };

  const formatDateOnly = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return [year, month, day].join('-');
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchTopScores = async () => {
      if (activeTab !== "ranking") return;

      setTopScoresLoading(true);

      try {
        const today = formatDateOnly(new Date());
        const result = await api.apiAssetsTransitScoresTop("Investment", today, 10);

        if (!isCancelled) {
          setTopScores(result ?? null);
        }
      } catch (err) {
        console.error(err);
        if (!isCancelled) {
          setTopScores(null);
          goError();
        }
      } finally {
        if (!isCancelled) setTopScoresLoading(false);
      }
    };

    fetchTopScores();

    return () => { isCancelled = true; };
  }, [api, activeTab]);

  useEffect(() => {
    let isCancelled = false;

    const fetchFavoriteAssets = async () => {
      if (activeTab !== "watchlist") return;

      setFavoriteAssetsLoading(true);

      try {
        const result = await api.apiAssetsFavoritesGet();

        if (!isCancelled) {
          setFavoriteAssets(result);
        }
      } catch (err) {
        console.error(err);
        if (!isCancelled) {
          setFavoriteAssets([]);
          goError();
        }
      } finally {
        if (!isCancelled) setFavoriteAssetsLoading(false);
      }
    };

    fetchFavoriteAssets();

    return () => { isCancelled = true; };
  }, [api, activeTab, isFavorite]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let isCancelled = false;

    const fetchAssets = async () => {
      const query = debouncedQuery.trim();
      if (!query) {
        setAssets([]);
        return;
      }

      try {
        const response = await api.apiAssetsSearch(query);
        if (!isCancelled) {
          setAssets(response);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error(err);
          setAssets([]);
        }
      }
    };

    fetchAssets();

    return () => {
      isCancelled = true;
    };
  }, [api, debouncedQuery]);

  const logoBase64 = selectedAsset?.logoBase64;
  const logoMediaType = selectedAsset?.logoMediaType ?? 'image/png';
  const logoSrc = logoBase64 ? `data:${logoMediaType};base64,${logoBase64}` : null;

  useEffect(() => {
    if (!selectedAsset?.ticker) {
      setTransitLoading(false);
      setTransitWindowResult(null);
      return;
    }

    let isCancelled = false;
    const ticker = selectedAsset.ticker;

    const fetchTransitProfile = async () => {
      setTransitLoading(true);
      try {
        const startDate = new Date();
        const endDate = selectedRange === '30d'
          ? (() => {
            const nextDate = new Date(startDate);
            nextDate.setDate(nextDate.getDate() + 30);
            return nextDate;
          })()
          : addMonths(startDate, Number(selectedRange.replace('m', '')));

        const result = await api.apiAssetsTransitProfileWindow(
          ticker,
          'Investment',
          formatDateOnly(startDate),
          formatDateOnly(endDate)
        );
        if (isCancelled) {
          return;
        }

        setTransitWindowResult(result ?? null);
        setSelectedDayIndex(0);
        setSelectedCandleIndex(0);
      } catch (err) {
        console.error(err);
        if (!isCancelled) {
          setTransitWindowResult(null);
        }
      } finally {
        if (!isCancelled) {
          setTransitLoading(false);
        }
      }
    };

    fetchTransitProfile();

    return () => {
      isCancelled = true;
    };
  }, [api, selectedAsset, selectedRange]);

  const getAssetIcon = (type?: string) => {
    switch (type) {
      case "CS":
        return <Building2 className="h-4 w-4" />
      case "ETV":
        return <PieChart className="h-4 w-4" />
      case "crypto":
        return <Coins className="h-4 w-4" />
      case "fund":
        return <BarChart3 className="h-4 w-4" />
      case undefined:
      default:
        return <DollarSign className="h-4 w-4" />
    }
  }

  const handleAssetSelect = useCallback(async (asset: AssetListDto) => {
    if (!asset.id) {
      setSelectedAsset(null);
      setTransitWindowResult(null);
      setIsFavorite(false);
      setSelectedCandleIndex(0);
      return;
    }

    try {
      setTransitWindowResult(null);
      setSelectedCandleIndex(0);
      const response = await api.apiAssetsByticker(asset.ticker ?? '');
      setSelectedAsset(response);
      setIsFavorite(response.isFavorite ?? false);
    } catch (err) {
      console.error(err);
      setSelectedAsset(null);
      setTransitWindowResult(null);
      setIsFavorite(false);
      setSelectedCandleIndex(0);
    }
  }, [api]);

  const addToWatchlist = useCallback(async (ticker: string | undefined) => {
    if (!ticker) {
      return;
    }

    try {
      setIsTogglingWatchlist(true);
      await api.apiAssetsFavoritesPost(ticker);
      setIsFavorite(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsTogglingWatchlist(false);
    }
  }, [api]);

  const removeFromWatchlist = useCallback(async (ticker: string | undefined) => {
    if (!ticker) {
      return;
    }

    try {
      setIsTogglingWatchlist(true);
      await api.apiAssetsFavoritesDelete(ticker);
      setIsFavorite(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsTogglingWatchlist(false);
    }
  }, [api]);

  const closeAssetDetails = useCallback(() => {
    setSelectedAsset(null);
    setTransitWindowResult(null);
    setIsFavorite(false);
    setSelectedCandleIndex(0);
  }, []);

  const fetcher = useMemo(() => {
    return async () => {
      if (!selectedAsset?.natalChart) {
        throw new Error("Natal chart não disponível");
      }
      return selectedAsset.natalChart;
    };
  }, [selectedAsset]);


  const { chartData, aspects } = useNatalChart(fetcher);

  const getScoreStatusLabel = useCallback((score?: number) => {
    if ((score ?? 0) > 70) {
      return t('investmentStatusFavorable');
    }
    if ((score ?? 0) > 40) {
      return t('investmentStatusNeutral');
    }
    return t('investmentStatusCaution');
  }, [t]);

  const formatLocalizedDate = useCallback(
    (date: Date | undefined, options?: Intl.DateTimeFormatOptions) => date?.toLocaleDateString(locale, options) ?? '',
    [locale],
  );

  const getTransitDateLabel = useCallback(
    (index: number, options?: Intl.DateTimeFormatOptions) => (
      index === 0
        ? t('investmentToday')
        : formatLocalizedDate(transitEvaluations[index]?.date, options)
    ),
    [formatLocalizedDate, t, transitEvaluations],
  );

  const getRangeLabel = useCallback((range: TransitRange) => {
    switch (range) {
      case '3m':
        return t('investmentRange3Months');
      case '6m':
        return t('investmentRange6Months');
      case '12m':
        return t('investmentRange12Months');
      case '30d':
      default:
        return t('investmentRange30Days');
    }
  }, [t]);

  const selectedRangeLabel = useMemo(() => getRangeLabel(selectedRange), [getRangeLabel, selectedRange]);

  const selectedTransitEvaluation = transitEvaluations[selectedDayIndex];

  const currentTransitScore = isLongRange
    ? transitWindowResult?.avgPercentAbsolute
    : selectedTransitEvaluation?.percentAbsolute;

  const currentTransitStatusClass = (currentTransitScore ?? 0) > 70
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : (currentTransitScore ?? 0) > 40
      ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-red-100 text-red-800 border-red-200";

  const currentTransitStatusToneClass = (currentTransitScore ?? 0) > 70
    ? "bg-emerald-100 text-emerald-800"
    : (currentTransitScore ?? 0) > 40
      ? "bg-amber-100 text-amber-800"
      : "bg-red-100 text-red-800";

  const transitCandles = useMemo(
    () => (isLongRange ? buildTransitCandles(transitEvaluations) : []),
    [isLongRange, transitEvaluations],
  );

  const selectedTransitCandle = transitCandles[selectedCandleIndex];

  const topTransitForecasts = useMemo(
    () => (isLongRange
      ? aggregateAspectForecasts(transitEvaluations)
      : selectedTransitEvaluation?.aspectForecasts?.slice()
        .sort((a, b) => Math.abs(b.contribution ?? 0) - Math.abs(a.contribution ?? 0))
        .slice(0, 5) ?? []),
    [isLongRange, selectedTransitEvaluation?.aspectForecasts, transitEvaluations],
  );

  const transitPeriodLabel = useMemo(() => {
    const firstDate = transitEvaluations[0]?.date;
    const lastDate = transitEvaluations[transitEvaluations.length - 1]?.date;

    if (!firstDate || !lastDate) {
      return selectedRangeLabel;
    }

    return `${formatLocalizedDate(firstDate, { day: 'numeric', month: 'short' })} - ${formatLocalizedDate(lastDate, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }, [formatLocalizedDate, selectedRangeLabel, transitEvaluations]);

  const chartAxisLabels = useMemo(() => {
    if (!transitEvaluations.length) {
      return {
        start: isLongRange ? selectedRangeLabel : t('investmentToday'),
        end: isLongRange ? selectedRangeLabel : t('investmentPlusDays', { count: 30 }),
      };
    }

    if (isLongRange) {
      return {
        start: formatLocalizedDate(transitEvaluations[0]?.date, { day: 'numeric', month: 'short' }),
        end: formatLocalizedDate(transitEvaluations[transitEvaluations.length - 1]?.date, { day: 'numeric', month: 'short', year: 'numeric' }),
      };
    }

    return {
      start: t('investmentToday'),
      end: t('investmentPlusDays', { count: 30 }),
    };
  }, [formatLocalizedDate, isLongRange, selectedRangeLabel, t, transitEvaluations]);

  const getCandleOffset = useCallback(
    (score: number) => ((100 - clampScore(score)) / 100) * CANDLE_CHART_HEIGHT,
    [],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800">{t('investmentTitle')}</h2>
          <p className="mt-1 text-slate-600">{t('investmentSubtitle')}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex flex-col md:grid md:grid-cols-3 w-full bg-white border border-stone-200 shadow-sm h-auto p-1 gap-1">
          <TabsTrigger value="ranking" className="w-full data-[state=active]:bg-action-100 justify-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            {t('investmentTabRanking')}
          </TabsTrigger>
          <TabsTrigger value="search" className="w-full data-[state=active]:bg-action-100 justify-center">
            <Search className="h-4 w-4 mr-2" />
            {t('investmentTabSearch')}
          </TabsTrigger>
          <TabsTrigger value="watchlist" className="w-full data-[state=active]:bg-action-100 justify-center">
            <Star className="h-4 w-4 mr-2" />
            {t('investmentTabWatchlist')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ranking" className="space-y-6">
          {topScoresLoading ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Skeleton - Top 10 Best */}
                <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center">
                      <div className="h-5 w-5 mr-2 rounded bg-emerald-200 animate-pulse" />
                      <div className="h-5 w-44 bg-emerald-200 rounded animate-pulse" />
                    </div>
                    <div className="h-4 w-64 bg-emerald-100 rounded animate-pulse mt-2" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 bg-white/80 rounded-lg border border-emerald-100 animate-pulse"
                        style={{ animationDelay: `${i * 80}ms` }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 rounded-full bg-emerald-200" />
                          <div className="w-8 h-8 rounded-lg bg-emerald-100" />
                          <div className="space-y-1.5">
                            <div className="h-4 w-16 bg-emerald-200 rounded" />
                            <div className="h-3 w-24 bg-emerald-100 rounded" />
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="h-5 w-12 bg-emerald-200 rounded-full" />
                          <div className="h-4 w-14 bg-emerald-100 rounded" />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Skeleton - Top 10 Worst */}
                <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200 shadow-sm">
                  <CardHeader>
                    <div className="flex items-center">
                      <div className="h-5 w-5 mr-2 rounded bg-red-200 animate-pulse" />
                      <div className="h-5 w-48 bg-red-200 rounded animate-pulse" />
                    </div>
                    <div className="h-4 w-60 bg-red-100 rounded animate-pulse mt-2" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 bg-white/80 rounded-lg border border-red-100 animate-pulse"
                        style={{ animationDelay: `${i * 80}ms` }}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 rounded-full bg-red-200" />
                          <div className="w-8 h-8 rounded-lg bg-red-100" />
                          <div className="space-y-1.5">
                            <div className="h-4 w-16 bg-red-200 rounded" />
                            <div className="h-3 w-24 bg-red-100 rounded" />
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="h-5 w-12 bg-red-200 rounded-full" />
                          <div className="h-4 w-14 bg-red-100 rounded" />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
              <div className="flex items-center justify-center py-4">
                <Sparkles className="h-5 w-5 mr-2 text-action-500 animate-spin" />
                <span className="text-sm text-slate-500">{t('investmentLoadingAssets')}</span>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-slate-800 flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2 text-emerald-600" />
                    {t('investmentTopPositiveTitle')}
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {t('investmentTopPositiveDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {topScores?.topPositive?.map((asset, index) => (
                    <div
                      key={asset.ticker}
                      className="flex items-center justify-between p-3 bg-white/80 rounded-lg border border-emerald-100 hover:border-emerald-300 cursor-pointer transition-colors"
                      onClick={() => handleAssetSelect(asset)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center overflow-hidden border border-stone-200">
                          {asset.logoBase64 ? (
                            <img
                              src={`data:${asset.logoMediaType ?? 'image/png'};base64,${asset.logoBase64}` || "/assets/placeholder.svg"}
                              alt={`${asset.name} logo`}
                              className="w-6 h-6 object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = "none"
                                e.currentTarget.nextElementSibling?.classList.remove("hidden")
                              }}
                            />
                          ) : null}
                          <div className={`flex items-center justify-center text-slate-600 ${asset.logoBase64 ? "hidden" : ""}`}>
                            {getAssetIcon(asset.type)}
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{asset.ticker}</div>
                          <div className="text-xs text-slate-500">{asset.name}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                          {asset.percentAbsolute}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-slate-800 flex items-center">
                    <TrendingDown className="h-5 w-5 mr-2 text-red-600" />
                    {t('investmentTopNegativeTitle')}
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {t('investmentTopNegativeDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {topScores?.topNegative?.map((asset, index) => (
                    <div
                      key={asset.ticker}
                      className="flex items-center justify-between p-3 bg-white/80 rounded-lg border border-red-100 hover:border-red-300 cursor-pointer transition-colors"
                      onClick={() => handleAssetSelect(asset)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center text-red-700 font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center overflow-hidden border border-stone-200">
                          {asset.logoBase64 ? (
                            <img
                              src={`data:${asset.logoMediaType ?? 'image/png'};base64,${asset.logoBase64}` || "/assets/placeholder.svg"}
                              alt={`${asset.name} logo`}
                              className="w-6 h-6 object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = "none"
                                e.currentTarget.nextElementSibling?.classList.remove("hidden")
                              }}
                            />
                          ) : null}
                          <div className={`flex items-center justify-center text-slate-600 ${asset.logoBase64 ? "hidden" : ""}`}>
                            {getAssetIcon(asset.type)}
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{asset.ticker}</div>
                          <div className="text-xs text-slate-500">{asset.name}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Badge className="bg-red-100 text-red-700 border-red-200">
                          {asset.percentAbsolute}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="search" className="space-y-6">
          <div className="space-y-6">
            <Card className="bg-white border-stone-200 shadow-sm">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={t('investmentSearchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-white border-stone-300 text-slate-800 placeholder-slate-400"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {searchQuery && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map((asset) => (
                <Card
                  key={asset.ticker}
                  className="bg-white border-stone-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleAssetSelect(asset)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        {asset.logoBase64 ? (
                          <img
                            src={`data:${asset.logoMediaType ?? 'image/png'};base64,${asset.logoBase64}` || "/assets/placeholder.svg"}
                            alt={`${asset.name} logo`}
                            className="w-12 h-12 object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none"
                              e.currentTarget.nextElementSibling?.classList.remove("hidden")
                            }}
                          />
                        ) : null}
                        <div className={`flex items-center justify-center w-12 h-12 ${asset.logoBase64 ? "hidden" : ""}`}>
                          {getAssetIcon(asset.type)}
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800">{asset.ticker}</h4>
                          <p className="text-xs text-slate-600">{asset.name}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="border-slate-300 text-slate-600 text-xs">
                        {asset.type?.toUpperCase()}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="watchlist" className="space-y-6">
          <Card className="bg-white border-stone-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center">
                <Star className="h-5 w-5 mr-2 text-yellow-500" />
                {t('investmentWatchlistTitle')}
              </CardTitle>
              <CardDescription className="text-slate-600">
                {t('investmentWatchlistDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {favoriteAssets.length === 0 ? (
                  <div className="text-center py-12">
                    <Star className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="mb-2 text-lg font-medium text-slate-800">{t('investmentEmptyTitle')}</h3>
                    <p className="mb-4 text-slate-600">{t('investmentEmptyDescription')}</p>
                    <Button onClick={() => setActiveTab("search")} className="bg-action-700 hover:bg-action-800">
                      {t('investmentTabSearch')}
                    </Button>
                  </div>
                ) : (
                  favoriteAssets.map((asset) => {
                    return (
                      <div
                        key={asset.ticker}
                        className="cursor-pointer rounded-lg border border-stone-200 p-4 transition-shadow hover:shadow-md"
                        onClick={() => handleAssetSelect(asset)}>
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="flex min-w-0 items-center space-x-3">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone-200 bg-stone-100">
                              {asset.logoBase64 ? (
                                <img
                                  src={`data:${asset.logoMediaType ?? 'image/png'};base64,${asset.logoBase64}` || "/assets/placeholder.svg"}
                                  alt={`${asset.name} logo`}
                                  className="h-8 w-8 object-contain"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none"
                                    e.currentTarget.nextElementSibling?.classList.remove("hidden")
                                  }}
                                />
                              ) : null}
                              <div className={`flex items-center justify-center text-slate-600 ${asset.logoBase64 ? "hidden" : ""}`}>
                                {getAssetIcon(asset.type)}
                              </div>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="text-lg font-bold text-slate-800">{asset.ticker}</h4>
                                <Badge variant="outline" className="border-slate-300 text-[10px] text-slate-600 sm:text-xs">
                                  {asset.type?.toUpperCase()}
                                </Badge>
                              </div>
                              <p className="mt-0.5 break-words pr-2 text-sm leading-5 text-slate-600">{asset.name}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 md:flex md:items-center md:space-x-6 md:gap-0">
                            <div className="rounded-lg bg-stone-50 px-3 py-2 text-left md:bg-transparent md:px-0 md:py-0 md:text-center">
                              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500 md:text-sm md:normal-case md:tracking-normal">{t('investmentAstroScore')}</div>
                              <Badge className={
                                (asset.score ?? 0) > 70
                                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                  : (asset.score ?? 0) > 40
                                    ? "bg-amber-100 text-amber-800 border-amber-200"
                                    : "bg-red-100 text-red-800 border-red-200"
                              }>
                                <Star className="mr-1 h-3 w-3" />
                                {asset.score}%
                              </Badge>
                            </div>
                            <div className="flex items-center justify-start md:justify-center">
                              <Badge className={
                                (asset.score ?? 0) > 70
                                  ? "w-full justify-center bg-emerald-100 text-emerald-800 border-emerald-200 md:w-auto"
                                  : (asset.score ?? 0) > 40
                                    ? "w-full justify-center bg-amber-100 text-amber-800 border-amber-200 md:w-auto"
                                    : "w-full justify-center bg-red-100 text-red-800 border-red-200 md:w-auto"
                              }>
                                {(asset.score ?? 0) > 70 ? (
                                  <><CheckCircle className="mr-1 h-4 w-4" /> {t('investmentStatusFavorable')}</>
                                ) : (asset.score ?? 0) > 40 ? (
                                  <><Activity className="mr-1 h-4 w-4" /> {t('investmentStatusNeutral')}</>
                                ) : (
                                  <><AlertTriangle className="mr-1 h-4 w-4" /> {t('investmentStatusCaution')}</>
                                )}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {selectedAsset && (
          <div
            className="fixed inset-0 z-50 flex items-stretch justify-stretch overflow-hidden bg-white md:items-center md:justify-center md:bg-black/50 md:p-4"
            style={{ paddingTop: 'var(--app-safe-top)', paddingBottom: 'var(--app-safe-bottom)' }}
          >
            <Card className="flex h-full w-full flex-col overflow-hidden border-0 bg-white shadow-none md:max-h-[90vh] md:max-w-4xl md:rounded-3xl md:border md:border-stone-200 md:shadow-xl">
              <CardHeader className="shrink-0 border-b border-stone-200 bg-white px-4 pb-4 pt-3 md:px-6 md:pb-6 md:pt-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="flex items-start gap-3 md:gap-4">
                    <Button
                      variant="ghost"
                      onClick={closeAssetDetails}
                      className="mt-0.5 h-10 w-10 shrink-0 p-0 text-slate-600 hover:bg-stone-100 md:hidden"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone-200 bg-stone-100">
                      {logoSrc ? (
                        <img
                          src={logoSrc || "/assets/placeholder.svg"}
                          alt={`${selectedAsset.name} logo`}
                          className="w-10 h-10 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none"
                            e.currentTarget.nextElementSibling?.classList.remove("hidden")
                          }}
                        />
                      ) : null}
                      <div className={`flex items-center justify-center ${logoSrc ? "hidden" : ""}`}>
                        {getAssetIcon(selectedAsset.type)}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-slate-800">{selectedAsset.ticker}</CardTitle>
                      <CardDescription className="text-slate-600">{selectedAsset.name}</CardDescription>
                    </div>
                  </div>
                  <div className="flex w-full items-center gap-2 md:w-auto md:pl-4">
                    <Button
                      variant="outline"
                      disabled={isTogglingWatchlist}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isFavorite) {
                          removeFromWatchlist(selectedAsset.ticker);
                        } else {
                          addToWatchlist(selectedAsset.ticker);
                        }
                      }}
                      className={
                        `flex-1 md:flex-none ${
                          isTogglingWatchlist
                            ? "border-slate-300 text-slate-400 cursor-wait"
                            : isFavorite
                              ? "border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                              : "border-slate-300 text-slate-600 hover:bg-slate-50"
                        }`
                      }
                    >
                      {isTogglingWatchlist ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t('processing')}
                        </>
                      ) :
                        isFavorite ? (
                          <>
                            <Star className="h-4 w-4 mr-2 fill-yellow-500" />
                            {t('investmentInWatchlist')}
                          </>
                        ) : (
                          <>
                            <StarOff className="h-4 w-4 mr-2" />
                            {t('investmentAddToWatchlist')}
                          </>
                        )}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={closeAssetDetails}
                      className="hidden text-slate-600 hover:text-slate-800 md:inline-flex"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-1 space-y-4 overflow-y-auto px-4 pb-6 pt-4 md:space-y-6 md:p-6">
                {assetDescription && (
                  <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                    <h4 className="mb-2 text-sm font-medium text-slate-700">{t('investmentAboutAsset', { name: selectedAsset.name })}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{assetDescription}</p>
                  </div>
                )}

                <Tabs defaultValue="transits" className="w-full">
                  <div className="sticky top-0 z-10 -mx-4 mb-4 bg-white/95 px-4 pb-3 pt-1 backdrop-blur md:static md:m-0 md:bg-transparent md:p-0">
                    <TabsList className="grid h-auto w-full grid-cols-2 gap-1 border border-stone-200 bg-stone-100 p-1">
                    <TabsTrigger value="transits" className="w-full data-[state=active]:bg-purple-100 data-[state=active]:text-purple-800 justify-center">
                      <Activity className="h-4 w-4 mr-2" />
                      {t('investmentTransitsTab')}
                    </TabsTrigger>
                    <TabsTrigger value="natal" className="w-full data-[state=active]:bg-action-100 data-[state=active]:text-action-800 justify-center">
                      <Sun className="h-4 w-4 mr-2" />
                      {t('investmentNatalChartTab')}
                    </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="transits" className="space-y-6 mt-6">
                    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                      <CardHeader className="space-y-4">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div>
                            <CardTitle className="flex items-center text-slate-800">
                              <Star className="mr-2 h-5 w-5 text-purple-500" />
                              {t('investmentTransitAnalysisRange', { range: selectedRangeLabel })}
                            </CardTitle>
                            <CardDescription className="mt-2 text-slate-600">
                              {transitLoading
                                ? t('investmentTransitLoadingDescription')
                                : isLongRange
                                  ? t('investmentTransitCandlestickDescription', { range: selectedRangeLabel })
                                  : t('investmentTransitClickBar')}
                            </CardDescription>
                          </div>

                          <div className="grid w-full grid-cols-4 gap-2 lg:w-auto">
                            {TRANSIT_RANGES.map((range) => (
                              <Button
                                key={range}
                                type="button"
                                variant={selectedRange === range ? 'default' : 'outline'}
                                onClick={() => setSelectedRange(range)}
                                className={
                                  selectedRange === range
                                    ? 'border-purple-600 bg-purple-600 text-white hover:bg-purple-700'
                                    : 'border-stone-300 bg-white text-slate-700 hover:bg-stone-50'
                                }
                              >
                                {range.toUpperCase()}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent>
                        <div className="space-y-4">
                          <div className="rounded-lg border border-indigo-200 bg-white p-4">
                            <div className="overflow-x-auto -mx-1 px-1 pb-2">
                              {transitLoading ? (
                                isLongRange ? (
                                  <div className="relative h-48 min-w-[560px]">
                                    <div className="flex h-full items-stretch gap-2">
                                      {Array.from({ length: 16 }).map((_, i) => (
                                        <div key={i} className="relative min-w-[14px] flex-1">
                                          <div
                                            className="absolute left-1/2 w-px -translate-x-1/2 rounded-full bg-purple-200 animate-pulse"
                                            style={{
                                              top: `${Math.random() * 60 + 20}px`,
                                              height: `${Math.random() * 60 + 50}px`,
                                              animationDelay: `${i * 40}ms`,
                                            }}
                                          />
                                          <div
                                            className="absolute left-1/2 w-3 -translate-x-1/2 rounded-sm border border-purple-300 bg-purple-200 animate-pulse"
                                            style={{
                                              top: `${Math.random() * 90 + 30}px`,
                                              height: `${Math.random() * 40 + 16}px`,
                                              animationDelay: `${i * 40}ms`,
                                            }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex h-48 min-w-[400px] items-end gap-1">
                                    {Array.from({ length: 30 }).map((_, i) => (
                                      <div key={i} className="flex flex-1 flex-col items-center">
                                        <div
                                          className="w-full min-w-[8px] rounded-sm bg-purple-200 animate-pulse"
                                          style={{
                                            height: `${Math.random() * 120 + 30}px`,
                                            animationDelay: `${i * 50}ms`,
                                          }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )
                              ) : isLongRange ? (
                                <div className="relative h-48 min-w-[560px]">
                                  <div className="flex h-full items-stretch gap-2">
                                    {transitCandles.map((candle, candleIndex) => {
                                      const openOffset = getCandleOffset(candle.open);
                                      const closeOffset = getCandleOffset(candle.close);
                                      const highOffset = getCandleOffset(candle.high);
                                      const lowOffset = getCandleOffset(candle.low);
                                      const bodyTop = Math.min(openOffset, closeOffset);
                                      const bodyHeight = Math.max(6, Math.abs(openOffset - closeOffset));
                                      const wickHeight = Math.max(2, lowOffset - highOffset);
                                      const isPositive = candle.close >= candle.open;
                                      const isSelectedCandle = selectedCandleIndex === candleIndex;

                                      return (
                                        <div
                                          key={candle.key}
                                          className={`relative min-w-[14px] flex-1 cursor-pointer rounded-sm transition-colors ${isSelectedCandle ? 'bg-purple-100/80 ring-2 ring-purple-500 ring-offset-1' : 'hover:bg-purple-50'}`}
                                          onClick={() => setSelectedCandleIndex(candleIndex)}
                                          title={t('investmentCandleTitle', {
                                            startDate: formatLocalizedDate(candle.startDate, { day: 'numeric', month: 'short' }),
                                            endDate: formatLocalizedDate(candle.endDate, { day: 'numeric', month: 'short' }),
                                            open: candle.open.toFixed(0),
                                            high: candle.high.toFixed(0),
                                            low: candle.low.toFixed(0),
                                            close: candle.close.toFixed(0),
                                          })}
                                        >
                                          <div
                                            className={`absolute left-1/2 w-px -translate-x-1/2 ${isPositive ? 'bg-emerald-500' : 'bg-red-500'}`}
                                            style={{ top: `${highOffset}px`, height: `${wickHeight}px` }}
                                          />
                                          <div
                                            className={`absolute left-1/2 w-3 -translate-x-1/2 rounded-sm border sm:w-4 ${isPositive ? 'border-emerald-500 bg-emerald-400/80' : 'border-red-500 bg-red-400/80'}`}
                                            style={{ top: `${bodyTop}px`, height: `${bodyHeight}px` }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex h-48 min-w-[400px] items-end gap-1">
                                  {transitEvaluations.map((evaluation, i) => {
                                    const percentAbsolute = evaluation.percentAbsolute ?? 0;
                                    const height = Math.max(5, (percentAbsolute / 100) * 180);
                                    const color =
                                      percentAbsolute > 70
                                        ? 'bg-emerald-400'
                                        : percentAbsolute > 40
                                          ? 'bg-amber-400'
                                          : 'bg-red-400';
                                    const isSelected = selectedDayIndex === i;

                                    return (
                                      <div key={i} className="flex flex-1 flex-col items-center">
                                        <div
                                          className={`${color} w-full min-w-[8px] rounded-sm transition-all ${isSelected ? 'ring-2 ring-purple-500 ring-offset-1' : 'cursor-pointer hover:opacity-80'}`}
                                          style={{ height: `${height}px` }}
                                          onClick={() => setSelectedDayIndex(i)}
                                          title={t('investmentBarTitle', {
                                            date: formatLocalizedDate(evaluation.date),
                                            score: percentAbsolute.toFixed(0),
                                            status: getScoreStatusLabel(percentAbsolute),
                                          })}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="mt-2 flex justify-between text-xs text-slate-500">
                              <span>{chartAxisLabels.start}</span>
                              <span>{chartAxisLabels.end}</span>
                            </div>

                            {!isLongRange && (
                              <p className="mt-1 text-xs text-slate-400 md:hidden">{t('investmentSwipeAllDays')}</p>
                            )}
                          </div>

                          {transitLoading ? (
                            <div className="rounded-lg border border-purple-200 bg-purple-100 p-3 animate-pulse">
                              <div className="flex items-center justify-center space-x-2">
                                <Sparkles className="h-4 w-4 animate-spin text-purple-500" />
                                <span className="text-sm text-purple-700">{t('investmentAnalyzingPlanetaryConfigurations')}</span>
                              </div>
                            </div>
                          ) : isLongRange && selectedTransitCandle ? (
                            <div className="rounded-lg border border-purple-200 bg-purple-100 p-3">
                              <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                  <div>
                                    <span className="font-medium text-purple-800">
                                      {t('investmentSelectedCandleTitle')}
                                    </span>
                                    <p className="text-xs text-purple-600">
                                      {formatLocalizedDate(selectedTransitCandle.startDate, { day: 'numeric', month: 'short' })} - {formatLocalizedDate(selectedTransitCandle.endDate, { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </p>
                                  </div>
                                  <Badge className={
                                    selectedTransitCandle.close > 70
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : selectedTransitCandle.close > 40
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-red-100 text-red-800'
                                  }>
                                    {t('investmentPercentWithStatus', {
                                      score: selectedTransitCandle.close.toFixed(0),
                                      status: getScoreStatusLabel(selectedTransitCandle.close),
                                    })}
                                  </Badge>
                                </div>

                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                  <div className="rounded-lg border border-purple-200 bg-white/80 p-3">
                                    <div className="text-xs uppercase tracking-wide text-slate-500">{t('investmentCandleOpenLabel')}</div>
                                    <div className="mt-1 font-semibold text-slate-800">{selectedTransitCandle.open.toFixed(0)}%</div>
                                  </div>
                                  <div className="rounded-lg border border-purple-200 bg-white/80 p-3">
                                    <div className="text-xs uppercase tracking-wide text-slate-500">{t('investmentCandleHighLabel')}</div>
                                    <div className="mt-1 font-semibold text-slate-800">{selectedTransitCandle.high.toFixed(0)}%</div>
                                  </div>
                                  <div className="rounded-lg border border-purple-200 bg-white/80 p-3">
                                    <div className="text-xs uppercase tracking-wide text-slate-500">{t('investmentCandleLowLabel')}</div>
                                    <div className="mt-1 font-semibold text-slate-800">{selectedTransitCandle.low.toFixed(0)}%</div>
                                  </div>
                                  <div className="rounded-lg border border-purple-200 bg-white/80 p-3">
                                    <div className="text-xs uppercase tracking-wide text-slate-500">{t('investmentCandleCloseLabel')}</div>
                                    <div className="mt-1 font-semibold text-slate-800">{selectedTransitCandle.close.toFixed(0)}%</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : currentTransitScore !== undefined ? (
                            <div className="rounded-lg border border-purple-200 bg-purple-100 p-3">
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <span className="font-medium text-purple-800">
                                    {isLongRange
                                      ? transitPeriodLabel
                                      : getTransitDateLabel(selectedDayIndex, { weekday: 'long', day: 'numeric', month: 'long' })}
                                  </span>
                                  {isLongRange && (
                                    <p className="text-xs text-purple-600">{t('investmentPeriodSummary')}</p>
                                  )}
                                </div>
                                <Badge className={currentTransitStatusToneClass}>
                                  {t('investmentPercentWithStatus', {
                                    score: currentTransitScore.toFixed(0),
                                    status: getScoreStatusLabel(currentTransitScore),
                                  })}
                                </Badge>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
                      <CardHeader>
                        <CardTitle className="flex items-center text-slate-800">
                          <Sparkles className={`mr-2 h-5 w-5 text-purple-500 ${transitLoading ? 'animate-spin' : ''}`} />
                          {transitLoading
                            ? t('investmentAnalysisLoading')
                            : isLongRange
                              ? t('investmentAnalysisTitleForRange', { range: selectedRangeLabel })
                              : t('investmentAnalysisTitleForDate', {
                                date: getTransitDateLabel(selectedDayIndex, { day: 'numeric', month: 'short' }),
                              })}
                        </CardTitle>
                        {!transitLoading && currentTransitScore !== undefined && (
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <Badge className={currentTransitStatusClass}>
                              {(currentTransitScore ?? 0) > 70 ? (
                                <><CheckCircle className="mr-1 h-4 w-4" /> {t('investmentStatusFavorable')}</>
                              ) : (currentTransitScore ?? 0) > 40 ? (
                                <><Activity className="mr-1 h-4 w-4" /> {t('investmentStatusNeutral')}</>
                              ) : (
                                <><AlertTriangle className="mr-1 h-4 w-4" /> {t('investmentStatusCaution')}</>
                              )}
                            </Badge>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-slate-600">
                                {isLongRange ? t('investmentAverageScoreLabel') : t('investmentScoreLabel')}
                              </span>
                              <span className="font-bold text-slate-800">{currentTransitScore.toFixed(0)}%</span>
                            </div>
                          </div>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {transitLoading ? (
                          <div className="space-y-4">
                            <div className="rounded-lg border border-purple-200 bg-white/80 p-4 animate-pulse">
                              <div className="mb-3 h-4 w-32 rounded bg-purple-200" />
                              <div className="space-y-2">
                                <div className="h-3 w-full rounded bg-purple-100" />
                                <div className="h-3 w-4/5 rounded bg-purple-100" />
                              </div>
                            </div>
                            <div className="rounded-lg border border-purple-200 bg-white/80 p-4 animate-pulse">
                              <div className="mb-3 h-4 w-28 rounded bg-purple-200" />
                              <div className="space-y-2">
                                <div className="h-12 w-full rounded bg-purple-100" />
                                <div className="h-12 w-full rounded bg-purple-100" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            {isLongRange && transitWindowResult?.globalForecast && (
                              <div className="rounded-lg border border-purple-200 bg-white/80 p-4">
                                <h4 className="mb-3 flex items-center font-medium text-slate-800">
                                  <Sparkles className="mr-2 h-4 w-4 text-purple-500" />
                                  {t('investmentPeriodSummary')}
                                </h4>
                                <p className="text-sm leading-relaxed text-slate-600">{transitWindowResult.globalForecast}</p>
                              </div>
                            )}

                            {topTransitForecasts.length > 0 && (
                              <div className="rounded-lg border border-purple-200 bg-white/80 p-4">
                                <h4 className="mb-3 flex items-center font-medium text-slate-800">
                                  <Star className="mr-2 h-4 w-4 text-yellow-500" />
                                  {isLongRange ? t('investmentTopTransitsOfPeriod') : t('investmentTopTransitsOfDay')}
                                </h4>
                                <div className="space-y-2">
                                  {topTransitForecasts.map((aspect, idx) => (
                                    <div
                                      key={idx}
                                      className={`rounded-lg border p-3 ${(aspect.contribution ?? 0) > 0
                                        ? 'border-emerald-200 bg-emerald-50'
                                        : (aspect.contribution ?? 0) < 0
                                          ? 'border-red-200 bg-red-50'
                                          : 'border-slate-200 bg-slate-50'
                                        }`}
                                    >
                                      <div className="mb-1 flex items-center justify-between gap-3">
                                        <span className="font-medium text-slate-800">
                                          {t('planet_' + aspect.transit)} - {t('planet_' + aspect.natal)}
                                        </span>
                                        <Badge
                                          variant="outline"
                                          className={
                                            (aspect.contribution ?? 0) > 0
                                              ? 'border-emerald-300 text-emerald-700'
                                              : (aspect.contribution ?? 0) < 0
                                                ? 'border-red-300 text-red-700'
                                                : 'border-slate-300 text-slate-600'
                                          }
                                        >
                                          {t('aspect_' + aspect.aspect)}
                                        </Badge>
                                      </div>
                                      {isLongRange && 'occurrences' in aspect && (
                                        <div className="text-xs text-slate-500">
                                          {t('investmentAspectOccurrences', { count: aspect.occurrences })}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="natal" className="space-y-6 mt-6">
                    <div className="relative w-full flex justify-center">
                      <div className="w-[320px] sm:w-[360px] md:w-[440px] lg:w-[480px] max-w-[130vw] aspect-square flex items-center justify-center mb-8">
                        {chartData && (
                          <AstroChart
                            showPlanetToggles={false}
                            planets={[
                              { name: Planets.Sun, sign: chartData.sun.sign, degree: chartData.sun.degree, house: chartData.sun.house },
                              { name: Planets.Moon, sign: chartData.moon.sign, degree: chartData.moon.degree, house: chartData.moon.house },
                              ...chartData.planets,
                            ]}
                            ascendant={chartData.ascendant}
                            descendant={chartData.descendant}
                            imumCoeli={chartData.imumCoeli}
                            mediumCoeli={chartData.mediumCoeli}
                            aspects={aspects}
                            houses={chartData.houses}
                            diameter={250}
                          />
                        )}
                      </div>
                    </div>

                    <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:items-stretch md:max-h-[500px] md:min-h-0">
                      <div className="flex flex-col rounded-lg border border-action-200 bg-white/80 p-4 md:max-h-[500px] md:overflow-hidden">
                        <h4 className="font-medium text-slate-800 mb-3 flex items-center shrink-0">
                          <Sun className="h-4 w-4 mr-2 text-orange-500" />
                          {t('planetaryPositions')}
                        </h4>
                        <div className="space-y-2 text-sm md:flex-1 md:min-h-0 md:overflow-y-auto">
                          {chartData && (
                            <div className="grid grid-cols-[90px_1fr_80px] items-center py-1">
                              <span className="text-slate-600">{t('planet_Sun')}</span>
                              <span className="font-medium text-slate-800 text-center">{t('sign_' + chartData.sun.sign)} • {t('house_' + chartData.sun.house)}</span>
                              <span className="font-medium text-slate-800 text-right">{degToDMSString(chartData.sun.degree - signPositions[chartData.sun.sign!])}</span>
                            </div>
                          )}
                          {chartData && (
                            <div className="grid grid-cols-[90px_1fr_80px] items-center py-1">
                              <span className="text-slate-600">{t('planet_Moon')}</span>
                              <span className="font-medium text-slate-800 text-center">{t('sign_' + chartData.moon.sign)} • {t('house_' + chartData.moon.house)}</span>
                              <span className="font-medium text-slate-800 text-right">{degToDMSString(chartData.moon.degree - signPositions[chartData.moon.sign!])}</span>
                            </div>
                          )}
                          {chartData && chartData.planets.map((planet, index) => (
                            <div key={index} className="grid grid-cols-[90px_1fr_80px] items-center py-1">
                              <span className="text-slate-600">
                                {t('planet_' + planet.name)}
                              </span>

                              <span className="font-medium text-slate-800 text-center">
                                {t('sign_' + planet.sign)} • {t('house_' + planet.house)}
                              </span>

                              <span className="font-medium text-slate-800 text-right">
                                {degToDMSString(planet.degree - signPositions[planet.sign!])}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-col rounded-lg border border-action-200 bg-white/80 p-4 md:max-h-[500px] md:overflow-hidden">
                        <h4 className="font-medium text-slate-800 mb-3 flex items-center shrink-0">
                          <Star className="h-4 w-4 mr-2 text-yellow-500" />
                          {t('aspects')}
                        </h4>
                        <div className="space-y-2 text-sm md:flex-1 md:min-h-0 md:overflow-y-auto">
                          {aspects.map((aspect, index) => (
                            <div key={index} className="grid grid-cols-[140px_1fr_80px] items-center py-1">
                              <span className="text-slate-600">
                                {t('planet_' + aspect.from)} - {t('planet_' + aspect.to)}
                              </span>
                              <span className="text-slate-600 text-center">
                                {t('orb')} {aspect.orb}°
                              </span>
                              <Badge
                                variant="secondary"
                                className={
                                  aspect.type === Aspects.Conjunction
                                    ? "bg-blue-100 text-blue-800 justify-self-end"
                                    : aspect.type === Aspects.Trine
                                      ? "bg-green-100 text-green-800 justify-self-end"
                                      : aspect.type === Aspects.Sextile
                                        ? "bg-emerald-100 text-emerald-800 justify-self-end"
                                        : "bg-slate-100 text-slate-800 justify-self-end"
                                }
                              >
                                {t('aspect_' + aspect.type)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                      <h4 className="font-medium text-slate-800 mb-3 flex items-center">
                        <Activity className="h-4 w-4 mr-2 text-amber-600" />
                        {t('angularPoints')}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-white/80 rounded-lg border border-amber-100 text-center">
                          <div className="text-xs font-semibold text-amber-700 mb-1">AC</div>
                          <div className="text-sm font-bold text-slate-800">{t('ascendant')}</div>
                          {chartData && (
                            <div className="text-xs text-slate-600 mt-1">
                              {t('sign_' + chartData.ascendant.sign)} {degToDMSString(chartData.ascendant.degree - signPositions[chartData.ascendant.sign!])}
                            </div>
                          )}
                        </div>
                        <div className="p-3 bg-white/80 rounded-lg border border-amber-100 text-center">
                          <div className="text-xs font-semibold text-blue-700 mb-1">DC</div>
                          <div className="text-sm font-bold text-slate-800">{t('descendant')}</div>
                          {chartData && (
                            <div className="text-xs text-slate-600 mt-1">
                              {t('sign_' + chartData.descendant.sign)} {degToDMSString(chartData.descendant.degree - signPositions[chartData.descendant.sign!])}
                            </div>
                          )}
                        </div>
                        <div className="p-3 bg-white/80 rounded-lg border border-amber-100 text-center">
                          <div className="text-xs font-semibold text-teal-700 mb-1">IC</div>
                          <div className="text-sm font-bold text-slate-800">{t('imumCoeli')}</div>
                          {chartData && (
                            <div className="text-xs text-slate-600 mt-1">
                              {t('sign_' + chartData.imumCoeli.sign)} {degToDMSString(chartData.imumCoeli.degree - signPositions[chartData.imumCoeli.sign!])}
                            </div>
                          )}
                        </div>
                        <div className="p-3 bg-white/80 rounded-lg border border-amber-100 text-center">
                          <div className="text-xs font-semibold text-purple-700 mb-1">MC</div>
                          <div className="text-sm font-bold text-slate-800">{t('mediumCoeli')}</div>
                          {chartData && (
                            <div className="text-xs text-slate-600 mt-1">
                              {t('sign_' + chartData.mediumCoeli.sign)} {degToDMSString(chartData.mediumCoeli.degree - signPositions[chartData.mediumCoeli.sign!])}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        )}
      </Tabs>
    </div >
  );
};

export default InvestmentAnalysis;
